import { AxiosError } from "axios";
import { ICheckoutBasket, IStoredCheckoutBasket } from "@checkout/types";
import { BaseAPI } from "@utils/base-api";

import { CheckoutStatus, type CheckoutBasket } from "@/primary-models";

import { CreateCheckoutBasketResponse } from "./types";

export class CheckoutAPI extends BaseAPI {

  private static readonly POLL_ATTEMPTS = 45;

  private static readonly POLL_INTERVAL_MS = 2000;

  // Legacy endpoint for creating a basket
  public async createBasket(
    userId: string,
    basket: ICheckoutBasket,
  ) {

    try {
      const response = await this.axios.post(`/internal/basket/${userId}`, {
        basket,
      });
      return { basket: response.data.basket as string };
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data?.existingBasket) {
        return { existingBasket: err.response.data.existingBasket as string };
      }

      throw err;
    }
  }

  // New endpoint for creating a basket
  public async createCheckoutBasket(
    userId: string,
    basket: IStoredCheckoutBasket,
  ): Promise<CreateCheckoutBasketResponse> {
    try {
      const response = await this.axios.post(`/v2/basket/${userId}`, {
        basket,
      });

      return response.data as CreateCheckoutBasketResponse;
    } catch (error) {

      console.error("Error creating checkout basket", error);
      // Network errors or 500s should be normalised
      return {
        basketId: "",
        isExisting: false,
        validationError: {
          field: "network",
          message: "Network request failed",
          errorType: "network_error",
        },
      };
    }
  }

  async getBasket(basketId: string) {

    const response = await this.axios.get<CheckoutBasket>(`/basket/${basketId}`);

    return response.data;
  }

  /** @return null if the basket was still in progress after poling for 90 seconds
   * or the not-in-progress basket, if one was found */
  async pollBasket(basketId: string): Promise<CheckoutBasket | null> {

    // Try every 2 secs for 90 seconds
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < CheckoutAPI.POLL_ATTEMPTS; i++) {

      const basket = await this.getBasket(basketId);

      if (basket.status !== CheckoutStatus.IN_PROGRESS && basket.status !== CheckoutStatus.DRAFT) {
        return basket;
      }

      await sleep(CheckoutAPI.POLL_INTERVAL_MS);
    }

    return null;

  }

}

const sleep = (n: number): Promise<void> => new Promise((res) => {
  setTimeout(() => {
    res();
  }, n);
});
